export type MoneyEdit =
  | { type: 'insert'; text: string }
  | { type: 'paste'; text: string }
  | { type: 'backspace' }
  | { type: 'delete' }

export interface MoneyEditResult {
  cents: number
  /** Caret position in `format(cents)`. */
  caret: number
}

/**
 * Where the caret lands, as the number of digits to its right — which no
 * reformatting changes — and which digit it sticks to. Whole-part edits stick
 * to the digit on the left, so the caret stays before the point; cents edits
 * stick to the digit on the right, so it stays after it.
 */
interface Caret {
  digitsToRight: number
  side: 'left' | 'right'
}

const CENTS_DIGITS = 2

const digitPositions = (text: string): number[] =>
  Array.from(text).flatMap((char, i) => (/\d/.test(char) ? [i] : []))

const digitsBefore = (text: string, position: number): number =>
  text.slice(0, position).replace(/\D/g, '').length

const placeCaret = (text: string, { digitsToRight, side }: Caret): number => {
  const positions = digitPositions(text)
  if (digitsToRight === 0) return text.length
  if (digitsToRight >= positions.length) return positions[0] ?? text.length
  const firstOnTheRight = positions.length - digitsToRight
  return side === 'left'
    ? positions[firstOnTheRight - 1] + 1
    : positions[firstOnTheRight]
}

/**
 * A pasted text written as an amount — with a decimal point, a thousands
 * comma or a dollar sign ("1234.56", "$1,234.56") — in cents. Null for bare
 * digits, which are typed at the caret like keys.
 */
const pastedAmountInCents = (text: string): number | null => {
  const trimmed = text.trim()
  if (!/[.,$]/.test(trimmed)) return null
  // A leading minus is dropped, as the digit-only parse always did, and only a
  // plain decimal is an amount: "$" alone, "1e5," or "0x1F," are not.
  const plain = trimmed.replace(/[$,\s]/g, '').replace(/^-/, '')
  if (!/^(\d+\.?\d*|\.\d+)$/.test(plain)) return null
  return Math.round(Number(plain) * 100)
}

const toCents = (digits: string[]): number =>
  parseInt(digits.join('') || '0', 10)

/**
 * Typed digits at a caret: appended at the end, written over cents digits,
 * inserted among whole digits. Pasted digits beyond the cents are dropped, so
 * a paste in the cents never changes the whole part.
 */
const insertAt = (
  digits: string[],
  at: number,
  typed: string,
  inCents: boolean,
  wholeDigits: number
): { digits: string[]; caret: Caret } => {
  if (at === digits.length) {
    return {
      digits: [...digits, ...typed],
      caret: { digitsToRight: 0, side: 'left' }
    }
  }
  if (inCents && at >= wholeDigits) {
    const next = [...digits]
    let i = at
    for (const digit of typed) {
      if (i === next.length) break
      next[i++] = digit
    }
    return {
      digits: next,
      caret: { digitsToRight: Math.max(0, next.length - i), side: 'right' }
    }
  }
  const next = [...digits.slice(0, at), ...typed, ...digits.slice(at)]
  return {
    digits: next,
    caret: { digitsToRight: next.length - at - typed.length, side: 'left' }
  }
}

/**
 * Applies a keystroke or a paste to a formatted amount and says where the caret
 * goes, keeping it where the person put it (EXP-2242).
 *
 * The amount stays a cash register at the end: a digit typed there shifts in
 * from the right ("1.23" + "4" → "12.34") and Backspace drops the last digit.
 * Anywhere else, whole-part digits are inserted and removed at the caret, and
 * cents digits are overwritten, so a key in the cents never moves the whole
 * part. A zero amount always takes digits at the end, wherever the caret is:
 * a click on "$0.00" lands mid-text, and typing there must still build the
 * amount from the right. A pasted amount ("$1,234.56") replaces the field.
 */
export const editMoney = (
  display: string,
  selectionStart: number,
  selectionEnd: number,
  requested: MoneyEdit,
  format: (cents: number) => string,
  clamp: (cents: number) => number = cents => cents
): MoneyEditResult => {
  // A pasted amount replaces the field, wherever the caret is.
  const pasted =
    requested.type === 'paste' ? pastedAmountInCents(requested.text) : null
  if (pasted !== null) {
    const cents = clamp(pasted)
    return { cents, caret: format(cents).length }
  }
  const edit: MoneyEdit =
    requested.type === 'paste'
      ? { type: 'insert', text: requested.text }
      : requested

  const sign = display.trimStart().startsWith('-') ? -1 : 1
  let digits = display.replace(/\D/g, '').split('')
  const wholeDigits = digits.length - CENTS_DIGITS
  const inCents = selectionStart > display.lastIndexOf('.')
  const start = digitsBefore(display, selectionStart)
  const end = digitsBefore(display, selectionEnd)
  const typed = edit.type === 'insert' ? edit.text.replace(/\D/g, '') : ''

  if (edit.type === 'insert' && !typed) {
    return { cents: sign * toCents(digits), caret: selectionStart }
  }

  let caret: Caret = { digitsToRight: 0, side: 'left' }
  if (toCents(digits) === 0) {
    digits = typed.split('')
  } else if (end > start) {
    // Selected whole digits go and selected cents become zero; what is typed
    // then goes where the selection started.
    digits = digits.flatMap((digit, i) => {
      if (i < start || i >= end) return [digit]
      return i < wholeDigits ? [] : ['0']
    })
    const removedWhole = Math.max(0, Math.min(end, wholeDigits) - start)
    caret = {
      digitsToRight: digits.length - start,
      side: inCents ? 'right' : 'left'
    }
    if (typed && toCents(digits) === 0) {
      digits = typed.split('')
      caret = { digitsToRight: 0, side: 'left' }
    } else if (typed) {
      ;({ digits, caret } = insertAt(
        digits,
        start,
        typed,
        inCents,
        wholeDigits - removedWhole
      ))
    }
  } else if (edit.type === 'insert') {
    ;({ digits, caret } = insertAt(digits, start, typed, inCents, wholeDigits))
  } else if (edit.type === 'backspace') {
    if (start === digits.length) {
      digits = digits.slice(0, -1)
    } else if (start === 0) {
      caret = { digitsToRight: digits.length, side: 'left' }
    } else if (start - 1 < wholeDigits) {
      digits = digits.filter((_, i) => i !== start - 1)
      caret = { digitsToRight: digits.length - start + 1, side: 'left' }
    } else {
      digits = digits.map((digit, i) => (i === start - 1 ? '0' : digit))
      caret = { digitsToRight: digits.length - start + 1, side: 'right' }
    }
  } else if (start < digits.length) {
    if (start < wholeDigits) {
      digits = digits.filter((_, i) => i !== start)
      caret = { digitsToRight: digits.length - start, side: 'left' }
    } else {
      digits = digits.map((digit, i) => (i === start ? '0' : digit))
      caret = { digitsToRight: digits.length - start - 1, side: 'right' }
    }
  }

  const unclamped = sign * toCents(digits)
  const cents = clamp(unclamped)
  const text = format(cents)
  if (cents !== unclamped || cents === 0) return { cents, caret: text.length }
  return { cents, caret: placeCaret(text, caret) }
}

/**
 * The edit behind a change the key handlers did not see — a cut, a paste from
 * the menu, typing from a keyboard that sends no key — read from the text and
 * the selection before it. Null when no digit was added or removed.
 */
export const editFromChange = (
  before: string,
  selectionStart: number,
  selectionEnd: number,
  after: string
): MoneyEdit | null => {
  const inserted = after.slice(
    selectionStart,
    after.length - (before.length - selectionEnd)
  )
  if (pastedAmountInCents(inserted) !== null && /\d/.test(inserted))
    return { type: 'paste', text: inserted }
  const typed = inserted.replace(/\D/g, '')
  if (typed) return { type: 'insert', text: typed }
  const removed =
    before.replace(/\D/g, '').length - after.replace(/\D/g, '').length
  if (removed <= 0) return null
  if (selectionEnd > selectionStart) return { type: 'backspace' }
  return after.slice(0, selectionStart) === before.slice(0, selectionStart)
    ? { type: 'delete' }
    : { type: 'backspace' }
}
